import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Eye, EyeSlash, GoogleLogo } from '@phosphor-icons/react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Form Section */}
      <div className="auth-form-section">
        <div className="w-full max-w-md mx-auto">
          {/* Logo */}
          <div className="mb-8">
            <img src="https://customer-assets.emergentagent.com/job_merchant-portal-112/artifacts/dsd8687t_Vinculum-logo-new.svg" alt="Vinculum" className="h-9" data-testid="login-logo" />
            <p className="text-sm text-[#5a5c87] mt-2">Marketplace Seller Management</p>
          </div>

          {/* Form */}
          <div className="space-y-6">
            <div>
              <h2 className="font-bold text-2xl text-[#0A0A0A]">Welcome back</h2>
              <p className="text-sm text-[#5a5c87] mt-1">Sign in to your seller account</p>
            </div>

            {error && (
              <div className="p-3 bg-[#ea4b5a]/10 border border-[#ea4b5a] text-[#c43447] text-sm rounded" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seller@company.com"
                  required
                  className="mt-1 rounded-sm border-[#e5e5ea] focus:ring-2 focus:ring-[#7d64ff] focus:border-[#7d64ff]"
                  data-testid="login-email"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                  Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="rounded-sm border-[#e5e5ea] focus:ring-2 focus:ring-[#7d64ff] focus:border-[#7d64ff] pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a5c87] hover:text-[#0A0A0A]"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#7d64ff] hover:bg-[#6153a7] text-white font-semibold py-2.5 rounded-sm transition-all duration-150"
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#e5e5ea]"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-4 bg-white text-[#5a5c87] uppercase tracking-wider">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={googleLogin}
              className="w-full border-[#e5e5ea] hover:bg-[#fcfcfc] font-medium py-2.5 rounded-sm transition-all duration-150"
              data-testid="google-login"
            >
              <GoogleLogo size={20} weight="bold" className="mr-2" />
              Sign in with Google
            </Button>

            <p className="text-center text-sm text-[#5a5c87]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#7d64ff] font-semibold hover:underline" data-testid="register-link">
                Register now
              </Link>
            </p>
          </div>
          <p className="text-[11px] text-[#9c9db7] text-center mt-8 tracking-wide" data-testid="auth-footer">
            Powered by <span className="font-semibold text-[#464C82]">Vinculum</span> &middot; &copy; {new Date().getFullYear()} Vinculum Group. All rights reserved.
          </p>
        </div>
      </div>

      {/* Media Section */}
      <div 
        className="auth-media-section"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/9cac01b7-677d-4f97-989d-99ea54579b96/images/2cd744aa84252903180bb901dfefc4d2a19ddfe8a38c1d18b2d7df2d453fdd41.png')`,
        }}
      >
        <div className="h-full w-full bg-gradient-to-br from-[#7d64ff]/20 to-transparent flex items-end p-12">
          <div className="text-white">
            <h2 className="font-black text-4xl mb-4">
              Manage Your<br />Marketplace Business
            </h2>
            <p className="text-lg opacity-90 max-w-md">
              Complete order management, inventory tracking, and financial reconciliation in one powerful platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
